import { configureStore, combineReducers } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import logger from 'redux-logger'; // Commented out - uncomment if needed for debugging
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage'; // Default: localStorage for web
import { PersistConfig } from 'redux-persist/lib/types';
import appReducer from '../containers/App/reducer';
import loginReducer from '../containers/Auth/Login/reducer';
import dashboardReducer from '../containers/Dashboard/reducer';
import dashboardSaga from '../containers/Dashboard/saga';
import savingWalletReducer from '../containers/SavingWallet/reducer';
import savingWalletSaga from '../containers/SavingWallet/saga';
import countryReducer from './countrySlice';
import config from '../config';

// Static reducers that are always present
const staticReducers = {
  app: appReducer,
  login: loginReducer,
  dashboard: dashboardReducer, // Add dashboard reducer as static to prevent caching issues
  savingWallet: savingWalletReducer, // Add savingWallet reducer as static to prevent table loading issues
  country: countryReducer, // Global country detection state
};

const isDevelopment = config.APP_ENV !== 'production';
// Create saga middleware
const sagaMiddleware = createSagaMiddleware();

// Persist configuration for specific reducers
const persistConfig: PersistConfig<any> = {
  key: 'root',
  storage,
  whitelist: ['app', 'login', 'dashboard', 'savingWallet', 'country'], // Persist country for SSR consistency
};

// Store enhancer for dynamic reducers
function createReducer(injectedReducers = {}) {
  // Use Partial to allow undefined for each reducer (for initial state)
  const rootReducer = {
    ...staticReducers,
    ...injectedReducers,
  };

  // If no reducers, return a dummy reducer with _persist property
  if (Object.keys(rootReducer).length === 0) {
    // Return a dummy reducer that initializes _persist state

    return (
      state = {
        _persist: { version: persistConfig.version ?? -1, rehydrated: false },
      },
    ) => state;
  }

  // Explicitly type the combined reducer state as Partial
  return persistReducer(
    persistConfig,
    combineReducers(rootReducer as Partial<typeof rootReducer>) as any,
  );
}

const middlewares = [sagaMiddleware];
if (isDevelopment) {
  middlewares.push(logger);
}

export function configureAppStore(
  initialState = { _persist: { version: -1, rehydrated: false } },
) {
  const store = configureStore({
    reducer: createReducer(),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        },
      }).concat(...middlewares) as any,
    // Uncomment the line below and comment out the above line if you need Redux logger for debugging:
    // }).concat(sagaMiddleware, logger) as any,

    devTools: process.env.NODE_ENV !== 'production',
  });

  // Extensions for dynamic loading
  // Store injection methods
  (store as any).injectedReducers = {} as Record<string, any>; // Reducer registry
  (store as any).injectedSagas = {} as Record<string, any>; // Saga registry
  (store as any).runSaga = sagaMiddleware.run; // Saga runner

  // Start static sagas immediately
  (store as any).runSaga(dashboardSaga);
  (store as any).runSaga(savingWalletSaga);

  // Function to inject reducers
  (store as any).injectReducer = (key: string, reducer: any) => {
    if ((store as any).injectedReducers[key]) {
      return; // Already injected
    }

    (store as any).injectedReducers[key] = reducer;
    store.replaceReducer(createReducer((store as any).injectedReducers) as any);
  };
  // Function to inject sagas
  (store as any).injectSaga = (key: string, saga: any) => {
    const hasSaga = Boolean((store as any).injectedSagas[key]);

    if (hasSaga) {
      (store as any).injectedSagas[key].task.cancel();
      (store as any).injectedSagas[key].task.toPromise().catch(() => { });
    }

    const task = (store as any).runSaga(saga);
    (store as any).injectedSagas[key] = { saga, task };

    // Return task to keep track of the saga execution
    return task;
  };

  // Function to eject reducers (optional)
  (store as any).ejectReducer = (key: string) => {
    if (!(store as any).injectedReducers[key]) {
      return;
    }

    delete (store as any).injectedReducers[key];
    store.replaceReducer(createReducer((store as any).injectedReducers) as any);
  };

  // Function to eject sagas (optional)
  (store as any).ejectSaga = (key: string) => {
    const injected = (store as any).injectedSagas[key];
    if (
      !injected ||
      !injected.task ||
      typeof injected.task.cancel !== 'function'
    ) {
      return;
    }
    injected.task.cancel();
    injected.task.toPromise().catch(() => { });
    delete (store as any).injectedSagas[key];
  };

  // Create persistor
  const persistor = persistStore(store);
  (store as any).persistor = persistor;

  // Make store available globally for API client token refresh
  if (typeof window !== 'undefined') {
    (window as any).__REDUX_STORE__ = store;
  }

  // Return correct type for store and persistor
  return { store, persistor } as const;
}
